"use client";

import { useRef, useState } from "react";

import Image from "next/image";
import { useTranslations } from "next-intl";
import ReCAPTCHA from "react-google-recaptcha";
import type { SubmitHandler } from "react-hook-form";
import { useForm } from "react-hook-form";
import { toast } from "sonner";

import { postEmailCode, verifyEmailCode } from "@shared/services/auth";
import { postEmail, verifyRecaptcha } from "@shared/services/inquiry";
import { AlertModal } from "@shared/ui/alert-modal";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from "@shared/ui/select";

import type { ModalStatus } from "@/types";

import { Button, cn, Input, Textarea } from "@seoul-moment/ui";

import { inquiryFormRezolver, type InquiryFormValues } from "../model/schema";

const RECAPTCHA_SITE_KEY = process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY ?? "";

// "기타(직접 입력)" 문의 유형의 select 값. 이 값일 때만 subject 입력이 활성화·검증된다.
const ETC_SUBJECT = "etc";

export default function InquiryForm() {
  const [isCodeSent, setIsCodeSent] = useState(false);
  const [modalOpen, setModalOpen] = useState<ModalStatus | null>(null);
  const [emailSubject, setEmailSubject] = useState("");
  const recaptchaRef = useRef<ReCAPTCHA>(null);
  const [recaptchaToken, setRecaptchaToken] = useState<string | null>(null);

  const t = useTranslations();
  const {
    register,
    handleSubmit,
    watch,
    trigger,
    setValue,
    setError,
    reset,
    clearErrors,
    formState: { errors },
  } = useForm<InquiryFormValues>({
    resolver: inquiryFormRezolver,
    defaultValues: {
      message: "",
      email: "",
      code: "",
      isVerified: false,
      name: "",
      subject: "",
    },
  });
  const formValues = watch();

  const isEmpty = Object.entries(formValues).some(([key, value]) => {
    if (key === "subject" && emailSubject !== ETC_SUBJECT) {
      return false;
    }

    return value === "" || value == null || value === false;
  });
  const isDisabled = Object.keys(errors).length > 0 || isEmpty;

  const handleClickVerify = async () => {
    const email = formValues.email;

    try {
      const isValid = await trigger("email");

      if (!isValid) {
        return;
      }

      await postEmailCode(email);
      setIsCodeSent(true);
    } catch {
      setIsCodeSent(false);
    }
  };

  const handleCheckCode = async () => {
    const { email, code } = formValues;

    if (!code || !email) return;

    try {
      await verifyEmailCode({ email, code });
      setValue("isVerified", true);
      clearErrors("code");
    } catch {
      setError("code", {
        message: "Code is incorrect, Please check again.",
      });
    }
  };

  const handleSelectSubject = (value: string) => {
    setEmailSubject(value);
    setValue("subject", "");
  };

  const handleOpen = (type: string) => (open: boolean) => {
    setModalOpen({
      type,
      open,
    });
  };

  const onSubmit: SubmitHandler<InquiryFormValues> = async (data) => {
    if (!recaptchaToken) {
      toast.error("Please complete the reCAPTCHA.", {
        position: "top-center",
      });
      return;
    }

    try {
      const { message, name, subject, email, isVerified } = data;

      await verifyRecaptcha(recaptchaToken);

      await postEmail({
        to: "seoulmomenttw@gmail.com",
        html: message,
        name,
        subject: emailSubject === ETC_SUBJECT ? subject : emailSubject,
        // 인증을 마친 문의자 이메일을 참조로 넣어 본인도 사본을 받게 한다.
        cc: isVerified ? [email] : undefined,
      });
      setModalOpen({
        type: "success",
        open: true,
      });
      setRecaptchaToken(null);
    } catch {
      setModalOpen({
        type: "error",
        open: true,
      });
    }
  };

  return (
    <>
      <form
        className="flex flex-col gap-[40px]"
        onSubmit={handleSubmit(onSubmit)}
      >
        <div className="flex flex-col gap-[20px]">
          <div>
            <Input
              className="h-[48px]"
              placeholder="Your Name"
              {...register("name")}
            />
            {errors.name && (
              <span className="text-error">{errors.name.message}</span>
            )}
          </div>
          <div>
            <div className="flex h-[48px] gap-[8px]">
              <Input
                placeholder="Please enter your email"
                {...register("email")}
                disabled={isCodeSent}
              />
              <Button
                className="h-[48px] w-[68px] whitespace-pre font-semibold"
                disabled={isCodeSent}
                onClick={handleClickVerify}
                type="button"
              >
                {t("verify")}
              </Button>
            </div>
            {errors.email && (
              <span className="text-error">{errors.email.message}</span>
            )}
            {!errors.email && isCodeSent && (
              <span className="text-sent">
                Verification code has been sent.
              </span>
            )}
          </div>
          {isCodeSent && (
            <>
              <div className="flex flex-col gap-[8px]">
                <div className="flex h-[48px] gap-[8px]">
                  <Input
                    placeholder="Please enter the verification code"
                    {...register("code")}
                  />
                  <Button
                    className="h-[48px] whitespace-pre"
                    disabled={!formValues.code || formValues.isVerified}
                    onClick={handleCheckCode}
                    type="button"
                  >
                    {formValues.isVerified ? "Verified" : "Confirm"}
                  </Button>
                </div>
                {!formValues.isVerified && errors.code && (
                  <span className="text-error">{errors.code.message}</span>
                )}
                {formValues.isVerified && (
                  <span className="text-sent">
                    Email verified successfully.
                  </span>
                )}
              </div>
            </>
          )}
          <div>
            <div className="flex h-[48px] gap-[8px]">
              <Select onValueChange={handleSelectSubject} value={emailSubject}>
                <SelectTrigger className="h-full max-w-[150px]">
                  {emailSubject.length === 0 ? t("select") : t(emailSubject)}
                </SelectTrigger>
                <SelectContent className="w-[150px]">
                  <SelectItem value="partnership_inquiry">
                    {t("partnership_inquiry")}
                  </SelectItem>
                  <SelectItem value="listing_inquiry">
                    {t("listing_inquiry")}
                  </SelectItem>
                  <SelectItem value={ETC_SUBJECT}>{t("etc")}</SelectItem>
                </SelectContent>
              </Select>
              <Input
                {...register("subject")}
                disabled={emailSubject !== ETC_SUBJECT}
              />
            </div>
            {errors.subject && (
              <span className="text-error">{errors.subject.message}</span>
            )}
          </div>
          <div>
            <Textarea
              className="h-[100px] resize-none"
              placeholder="Write your message(30 characters)"
              {...register("message")}
            />
            {errors.message && (
              <span className="text-error">{errors.message.message}</span>
            )}
          </div>
        </div>
        <div className="flex flex-col gap-[10px]">
          <Button
            className="h-auto w-full px-[20px] py-[16px]"
            disabled={isDisabled}
            type="submit"
          >
            Send Message
          </Button>
          <p className="text-body-3 max-sm:text-body-5 text-black/40">
            Protected by reCAPTCHA. Google Privacy Policy and Terms apply. By
            submitting, you agree to our use of your data to respond to your
            inquiry.
          </p>
          <div
            className={cn(
              "flex justify-center",
              "max-2xl:justify-start",
              "max-md:justify-center",
            )}
          >
            <ReCAPTCHA
              onChange={setRecaptchaToken}
              onError={() => setRecaptchaToken(null)}
              ref={recaptchaRef}
              sitekey={RECAPTCHA_SITE_KEY}
              size="normal"
            />
          </div>
        </div>
      </form>
      {modalOpen?.type === "success" && (
        <AlertModal
          onClickOK={() => {
            handleOpen("success")(false);
            reset();
            setIsCodeSent(false);
          }}
          onOpenChange={handleOpen("success")}
          open={modalOpen?.open}
        >
          <div className="flex flex-col items-center justify-center gap-[24px]">
            <Image alt="" height={40} src="/alert_complete.svg" width={40} />
            <p className="text-center">
              Your message has been sent <br />
              successfully. Thank you.
            </p>
          </div>
        </AlertModal>
      )}
      {modalOpen?.type === "error" && (
        <AlertModal
          onClickOK={() => handleOpen("error")(false)}
          onOpenChange={handleOpen("error")}
          open={modalOpen?.open}
        >
          <div className="flex flex-col items-center justify-center gap-[24px]">
            <Image alt="" height={40} src="/alert_error.svg" width={40} />
            <p className="text-center">
              An error occurred while sending.
              <br />
              Please try again later.
            </p>
          </div>
        </AlertModal>
      )}
    </>
  );
}
